export type ColorSpace =
  | "rgb"
  | "hsl"
  | "hwb"
  | "lab"
  | "lch"
  | "oklab"
  | "oklch";

export type CSSColorFormat = ColorSpace | "hex";

/** Color utility class. */
export class Color {
  /** Red channel. Float in range [0, 1] */
  r: number;
  /** Green channel. Float in range [0, 1] */
  g: number;
  /** Blue channel. Float in range [0, 1] */
  b: number;
  /** Alpha channel. Float in range [0, 1] */
  a: number;

  /** Red channel. Int in range [0, 255] */
  get R() {
    return min(max((this.r * 255 + 0.5) | 0, 0), 255);
  }
  set R(R: number) {
    this.r = R / 255;
  }
  /** Green channel. Int in range [0, 255] */
  get G() {
    return min(max((this.g * 255 + 0.5) | 0, 0), 255);
  }
  set G(G: number) {
    this.g = G / 255;
  }
  /** Blue channel. Int in range [0, 255] */
  get B() {
    return min(max((this.b * 255 + 0.5) | 0, 0), 255);
  }
  set B(B: number) {
    this.b = B / 255;
  }
  /** Alpha channel. Int in range [0, 255] */
  get A() {
    return min(max((this.a * 255 + 0.5) | 0, 0), 255);
  }
  set A(A: number) {
    this.a = A / 255;
  }

  /** Raw constructor for Color.
   *
   * Optionally accepts red, green, blue, and alpha values as floats within range [0, 1]. */
  constructor(r?: number, g?: number, b?: number, a?: number) {
    this.r = r ?? 0;
    this.g = g ?? 0;
    this.b = b ?? 0;
    this.a = a ?? 1;
  }

  /** Copy factory:
   *
   * Accepts another color and creates a copy. */
  static fromColor(color: Color) {
    return new Color(color.r, color.g, color.b, color.a);
  }
  /** Floating-point R,G,B factory:
   *
   * Create a color with red, green, and blue in range [0, 1]. */
  static fromRGB(red: number, green: number, blue: number, alpha?: number) {
    return new Color(red, green, blue, alpha);
  }
  /** Integer R,G,B factory:
   *
   * Create a color given red, green, and blue as integers in range [0, 255]. */
  static fromRGB256(red: number, green: number, blue: number, alpha?: number) {
    return new Color(
      red / 255,
      green / 255,
      blue / 255,
      alpha ? alpha / 255 : 1
    );
  }
  /** CSS color factory:
   *
   * Create a color given a CSS color string. */
  static fromCSS(css: string) {
    if (typeof css !== "string") {
      throw new Error("Color.fromCSS: 'css' must be a string.");
    }

    css = css.replace(/^\s+|\s+$/g, "").toLowerCase(); // trim
    if (css in cssNamedColors) css = "#" + cssNamedColors[css as CSSColorName];

    const alpha = (A?: string, AP?: string) => {
      const isSet = A && A !== "none";
      return isSet ? +A / (AP ? 100 : 1) : 1;
    };

    let m: RegExpMatchArray | null = null;
    if ((m = css.match(rgxHexColor))) {
      const [, h] = m;
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 255;
      switch (h.length) {
        case 3:
          [r, g, b] = [unhex(h[0], h[0]), unhex(h[1], h[1]), unhex(h[2], h[2])];
          break;
        case 4:
          [r, g, b, a] = [
            unhex(h[0], h[0]),
            unhex(h[1], h[1]),
            unhex(h[2], h[2]),
            unhex(h[3], h[3]),
          ];
          break;
        case 6:
          [r, g, b] = [unhex(h[0], h[1]), unhex(h[2], h[3]), unhex(h[4], h[5])];
          break;
        case 8:
          [r, g, b, a] = [
            unhex(h[0], h[1]),
            unhex(h[2], h[3]),
            unhex(h[4], h[5]),
            unhex(h[6], h[7]),
          ];
          break;
      }
      return new Color(r / 255, g / 255, b / 255, a / 255);
    }
    if ((m = css.match(rgxRgbColor))) {
      // r % g % b % a %
      const [, R, RP, G, GP, B, BP, A, AP] = m;
      const r = (R === "none" ? 0 : +R) / (RP ? 100 : 255);
      const g = (G === "none" ? 0 : +G) / (GP ? 100 : 255);
      const b = (B === "none" ? 0 : +B) / (BP ? 100 : 255);
      const a = alpha(A, AP);
      return new Color(r, g, b, a);
    }
    if ((m = css.match(rgxHslColor))) {
      // h unit s l a %
      const [, H, HT, S, L, A, AP] = m;
      const h =
        (H === "none" ? 0 : +H) *
        cssRotUnitTable[(HT || "deg") as CSSRotationUnit];
      const s = S === "none" ? 0 : +S;
      const l = L === "none" ? 0 : +L;
      const a = alpha(A, AP);
      const [r, g, b] = Color._hslToRgb(h, s, l);
      return new Color(r, g, b, a);
    }
    if ((m = css.match(rgxHwbColor))) {
      // h unit w b a %
      const [, H, HT, W, B, A, AP] = m;
      const h =
        (H === "none" ? 0 : +H) *
        cssRotUnitTable[(HT || "deg") as CSSRotationUnit];
      const wh = W === "none" ? 0 : +W;
      const bk = B === "none" ? 0 : +B;
      const a = alpha(A, AP);
      const [r, g, b] = Color._hwbToRgb(h, wh, bk);
      return new Color(r, g, b, a);
    }
    if ((m = css.match(rgxLabColor))) {
      // ok L % A % B % a %
      const [, ok, L, LP, lA, lAP, lB, lBP, A, AP] = m;
      if (ok) {
        const l = (L === "none" ? 0 : +L) * (LP ? 1 : 100);
        const la = (lA === "none" ? 0 : +lA) * (lAP ? 0.004 : 1);
        const lb = (lB === "none" ? 0 : +lB) * (lBP ? 0.004 : 1);
        const a = alpha(A, AP);
        const [r, g, b] = Color._oklabToRgb(l, la, lb);
        return new Color(r, g, b, a);
      } else {
        const l = L === "none" ? 0 : +L;
        const la = (lA === "none" ? 0 : +lA) * (lAP ? 1.25 : 1);
        const lb = (lB === "none" ? 0 : +lB) * (lBP ? 1.25 : 1);
        const a = alpha(A, AP);
        const [r, g, b] = Color._labToRgb(l, la, lb);
        return new Color(r, g, b, a);
      }
    }
    if ((m = css.match(rgxLchColor))) {
      // ok L % c % h unit a %
      const [, ok, L, LP, C, CP, H, HT, A, AP] = m;
      if (ok) {
        const l = (L === "none" ? 0 : +L) * (LP ? 1 : 100);
        const c = (C === "none" ? 0 : +C) * (CP ? 0.004 : 1);
        const h =
          (H === "none" ? 0 : +H) /
          cssRotUnitTable[(HT || "deg") as CSSRotationUnit];
        const a = alpha(A, AP);
        const [r, g, b] = Color._oklchToRgb(l, c, h);
        return new Color(r, g, b, a);
      } else {
        const l = L === "none" ? 0 : +L;
        const c = (C === "none" ? 0 : +C) * (CP ? 1.5 : 1);
        const h =
          (H === "none" ? 0 : +H) /
          cssRotUnitTable[(HT || "deg") as CSSRotationUnit];
        const a = alpha(A, AP);
        const [r, g, b] = Color._lchToRgb(l, c, h);
        return new Color(r, g, b, a);
      }
    }
    return new Color(0, 0, 0, 1); // default to black
  }
  /** HSL color factory:
   *
   * Create a color given a hue `[0, 360)`, saturation `[0, 100]`, and luminosity `[0, 100]`. */
  static fromHSL(
    hue: number,
    saturation: number,
    luminance: number,
    alpha?: number
  ) {
    const [r, g, b] = Color._hslToRgb(hue, saturation, luminance);
    return new Color(r, g, b, alpha);
  }
  /** HWB color factory:
   *
   * Create a color given a hue `[0, 360)`, white amount `[0, 100]`, and black amount
   * `[0, 100]`. */
  static fromHWB(hue: number, white: number, black: number, alpha?: number) {
    const [r, g, b] = Color._hwbToRgb(hue, white, black);
    return new Color(r, g, b, alpha);
  }
  /** XYZ color space factory:
   *
   * Create a color from XYZ color space, given values for X, Y, and Z. */
  static fromXYZ(x: number, y: number, z: number, alpha?: number) {
    const [r, g, b] = Color._xyzToRgb(x, y, z);
    return new Color(r, g, b, alpha);
  }
  /** Lab color space factory:
   *
   * Create a color from luminance `[0, 100]`, and a / b chromacity values `[-125, 125]`. */
  static fromLab(luminance: number, a: number, b: number, alpha?: number) {
    const [R, G, B] = Color._labToRgb(luminance, a, b);
    return new Color(R, G, B, alpha);
  }
  /** LCH color factory:
   *
   * Create a color from luminance `[0, 100]`, chromacity `[0, 150]`, and hue `[0, 360)`. */
  static fromLCH(
    luminance: number,
    chromacity: number,
    hue: number,
    alpha?: number
  ) {
    const [r, g, b] = Color._lchToRgb(luminance, chromacity, hue);
    return new Color(r, g, b, alpha);
  }
  /** OkLab color space factory:
   *
   * Create a color from luminance `[0, 100]`, and a / b chromacity values `[-0.4, 0.4]` in the
   * Oklab color space. */
  static fromOkLab(luminance: number, a: number, b: number, alpha?: number) {
    const [R, G, B] = Color._oklabToRgb(luminance, a, b);
    return new Color(R, G, B, alpha);
  }
  /** OkLCH color factory:
   *
   * Create a color from luminance `[0, 100]`, chromacity [0, 0.4], and hue `[0, 360)` in the
   * Oklab color space via lch-to-lab conversion within Oklab color space. */
  static fromOkLCH(
    luminance: number,
    chromacity: number,
    hue: number,
    alpha?: number
  ) {
    const [r, g, b] = Color._oklchToRgb(luminance, chromacity, hue);
    return new Color(r, g, b, alpha);
  }
  /** Factory for generic color space:
   *
   * Create a color in the given color space with the given values. */
  static fromColorSpaceValues(
    space: ColorSpace,
    x: number,
    y: number,
    z: number,
    a?: number
  ): Color {
    if (!(space in validColorSpaces)) {
      throw new Error(
        `Color.fromColorSpaceValues: Unknown color space: ${space}`
      );
    }
    switch (space) {
      case "rgb":
        return Color.fromRGB(x, y, z, a);
      case "hsl":
        return Color.fromHSL(x, y, z, a);
      case "hwb":
        return Color.fromHWB(x, y, z, a);
      case "lab":
        return Color.fromLab(x, y, z, a);
      case "lch":
        return Color.fromLCH(x, y, z, a);
      case "oklab":
        return Color.fromOkLab(x, y, z, a);
      case "oklch":
        return Color.fromOkLCH(x, y, z, a);
    }
  }

  /** Clone the color and return the copy. */
  clone() {
    return Color.fromColor(this);
  }

  /** Interpolate this color with another color, given a factor. */
  mix(color: Color, T: number) {
    return Color.mix(this, color, T);
  }
  /** Interpolate this color with another color, given a factor, within a given color space. */
  mixInSpace(space: ColorSpace, color: Color, T: number) {
    return Color.mixInSpace(space, this, color, T);
  }
  /** Interpolate two colors together, given a factor. */
  static mix(a: Color, b: Color, T: number) {
    const _T = 1 - T;
    return new Color(
      a.r * _T + b.r * T,
      a.g * _T + b.g * T,
      a.b * _T + b.b * T,
      a.a * _T + b.a * T
    );
  }
  /** Interpolate two colors together, given a factor, within a given color space. */
  static mixInSpace(space: ColorSpace, a: Color, b: Color, T: number) {
    if (!(space in validColorSpaces)) {
      throw new Error(`Color.mixInSpace: Unknown color space: ${space}`);
    }

    // convert colors to color-space values
    const ca = a.toColorSpaceValues(space);
    const cb = b.toColorSpaceValues(space);

    // determine which channels contain hue values
    const hueIn1 = space === "hsl" || space === "hwb";
    const hueIn3 = space === "lch" || space === "oklch";

    // apply interpolation
    const _T = 1 - T;
    const newValues = [
      hueIn1 ? Color._blendHue(ca[0], cb[0], T) : ca[0] * _T + cb[0] * T,
      ca[1] * _T + cb[1] * T,
      hueIn3 ? Color._blendHue(ca[2], cb[2], T) : ca[2] * _T + cb[2] * T,
      ca[3] * _T + cb[3] * T,
    ] as const;

    // convert back to rgb
    return Color.fromColorSpaceValues(space, ...newValues);
  }

  /** Raw-add RGB components of two colors. The new color is opaque, unless a new alpha value is
   * passed. */
  static add(a: Color, b: Color, alpha: number = 1) {
    return new Color(a.r + b.r, a.g + b.g, a.b + b.b, alpha);
  }
  /** Raw-subtract RGB components of the second color from the first. The new color is opaque,
   * unless a new alpha value is passed. */
  static subtract(a: Color, b: Color, alpha: number = 1) {
    return new Color(a.r - b.r, a.g - b.g, a.b - b.b, alpha);
  }
  /** Raw-multiply RGB components of two colors. The new color is opaque, unless a new alpha
   * value is passed. */
  static multiply(a: Color, b: Color, alpha: number = 1) {
    return new Color(a.r * b.r, a.g * b.g, a.b * b.b, alpha);
  }

  /** Get the distance between this color and another color, in a range from `[0 ,1]`. */
  distance(color: Color) {
    return Color.distance(this, color);
  }
  /** Get the perceptual distance between this color and another color, in a range from
   * `[0, 1]`.
   *
   * Slightly slower than `distance()`. */
  perceptualDistance(color: Color) {
    return Color.perceptualDistance(this, color);
  }
  /** Get the distance between two colors, in a range from `[0, 1]`. */
  static distance(a: Color, b: Color) {
    return (abs(a.r - b.r) + abs(a.g - b.g) + abs(a.b - b.b)) / 3;
  }
  /** Get the perceptual distance between two colors, in a range from `[0, 1]`.
   *
   * Slightly slower than `distance()`. */
  static perceptualDistance(a: Color, b: Color) {
    const dr = a.r - b.r;
    const dg = a.g - b.g;
    const db = a.b - b.b;
    return sqrt(dr * dr + dg * dg + db * db);
  }

  /** Get a CSS color string for this color. */
  css(format: CSSColorFormat = "hex") {
    switch (format) {
      case "rgb": {
        const a = this.a >= 1 ? "" : `, ${this.a}`;
        return `rgb(${this.R}, ${this.G}, ${this.B}${a})`;
      }
      case "hsl": {
        const [h, s, l] = Color._rgbToHsl(this.r, this.g, this.b);
        return `hsl(${h}deg ${s} ${l}${printSlashAlpha(this.a)})`;
      }
      case "hwb": {
        const [h, w, b] = Color._rgbToHwb(this.r, this.g, this.b);
        return `hwb(${h}deg ${w} ${b}${printSlashAlpha(this.a)})`;
      }
      case "lab": {
        const [l, A, B] = Color._rgbToLab(this.r, this.g, this.b);
        return `lab(${l}% ${A} ${B}${printSlashAlpha(this.a)})`;
      }
      case "lch": {
        const [l, c, h] = Color._rgbToLch(this.r, this.g, this.b);
        return `lch(${l}% ${c} ${h}deg${printSlashAlpha(this.a)})`;
      }
      case "oklab": {
        const [l, A, B] = Color._rgbToOklab(this.r, this.g, this.b);
        return `oklab(${l}% ${A} ${B}${printSlashAlpha(this.a)})`;
      }
      case "oklch": {
        const [l, c, h] = Color._rgbToOklch(this.r, this.g, this.b);
        return `oklch(${l}% ${c} ${h}deg${printSlashAlpha(this.a)})`;
      }
      case "hex":
      default: {
        const A = this.A;
        const alpha = A >= 255 ? "" : hex(A);
        return `#${hex(this.R)}${hex(this.G)}${hex(this.B)}${alpha}`;
      }
    }
  }

  /** Get the color as a packed dword, RGBA (R is in the highest value place).
   *
   * If rendered as a hexadecimal string, this value would match its CSS hex representation. */
  dwordRGBA() {
    return (this.R << 24) | (this.G << 16) | (this.B << 8) | this.A;
  }
  /** Get the color as a packed dword, ABGR (A is in the highest value place).
   *
   * If serialized as a little-endian dword, and interpreted as hex, the value would match its
   * CSS hex representation. */
  dwordABGR() {
    return (this.A << 24) | (this.B << 16) | (this.G << 8) | this.R;
  }

  /** Get the natural brightness of the color, in range [0, 1]. */
  brightness() {
    return 0.299 * this.r + 0.587 * this.g + 0.114 * this.b;
  }
  /** Get the perceived brightness of the color, in range [0, 1].
   *
   * Slightly slower to calculate than `brightness()`. */
  perceptualBrightness() {
    const r = this.r * this.r;
    const g = this.g * this.g;
    const b = this.b * this.b;
    return sqrt(0.299 * r + 0.587 * g + 0.114 * b);
  }
  /** Get the hue of the color. */
  hue() {
    return Color._rgbHue(this.r, this.g, this.b);
  }

  /** Get the red, green, blue, and alpha channels of the color in array-form. */
  rgb() {
    return [this.r, this.g, this.b, this.a];
  }
  /** Get the hue, saturation, value, and alpha of the color. */
  hsl() {
    const [h, s, l] = Color._rgbToHsl(this.r, this.g, this.b);
    return [h, s, l, this.a];
  }
  /** Get the hue, white, black, and alpha of the color. */
  hwb() {
    const [h, w, b] = Color._rgbToHwb(this.r, this.g, this.b);
    return [h, w, b, this.a];
  }
  /** Get the X, Y, and Z values of the color in XYZ color space. */
  xyz() {
    const [x, y, z] = Color._rgbToXyz(this.r, this.g, this.b);
    return [x, y, z, this.a];
  }
  /** Get the luminance, a / b chromacity values, and alpha of the color in Lab color space. */
  lab() {
    const [l, a, b] = Color._rgbToLab(this.r, this.g, this.b);
    return [l, a, b, this.a];
  }
  /** Get the luminance, chromacity, hue, and alpha of the color in Lab color space. */
  lch() {
    const [l, c, h] = Color._rgbToLch(this.r, this.g, this.b);
    return [l, c, h, this.a];
  }
  /** Get the luminance, and a / b chromacity values, and alpha of the color in OkLab color
   * space. */
  oklab() {
    const [l, a, b] = Color._rgbToOklab(this.r, this.g, this.b);
    return [l, a, b, this.a];
  }
  /** Get the luminance, chromacity, hue, and alpha of the color in OkLab color space. */
  oklch() {
    const [l, c, h] = Color._rgbToOklch(this.r, this.g, this.b);
    return [l, c, h, this.a];
  }

  /** Get the values of the color in the given color space. */
  toColorSpaceValues(space: ColorSpace) {
    if (!(space in validColorSpaces)) {
      throw new Error(
        `Color::toColorSpaceValues: Unknown color space: ${space}`
      );
    }
    return this[space]();
  }

  static readonly WHITE = new Color(1, 1, 1, 1);
  static readonly GRAY = new Color(0.5, 0.5, 0.5, 1);
  static readonly BLACK = new Color(0, 0, 0, 1);
  static readonly TRANSPARENT = new Color(0, 0, 0, 0);
  static readonly RED = new Color(1, 0, 0, 1);
  static readonly GREEN = new Color(0, 1, 0, 1);
  static readonly BLUE = new Color(0, 0, 1, 1);

  // PRIVATE

  // https://chromium.googlesource.com/chromium/src/%2B/986c5d0da3a7c6e6e48ec537d1cbe255582ccabe/third_party/blink/renderer/platform/graphics/color.cc

  // https://drafts.csswg.org/css-color/#hsl-to-rgb
  private static _hslToRgb(hue: number, sat: number, lum: number) {
    // hue [0, 360]  sat [0, 100]  lum [0, 100]
    hue = hue / 360;
    hue = (hue - floor(hue)) * 12;
    sat /= 100;
    lum /= 100;

    const f = (n: number) => {
      const k = (n + hue) % 12;
      const a = sat * min(lum, 1 - lum);
      return lum - a * max(-1, min(k - 3, 9 - k, 1));
    };
    return [f(0), f(8), f(4)];
  }
  // https://drafts.csswg.org/css-color/#rgb-to-hsl
  private static _rgbToHsl(r: number, g: number, b: number) {
    const mn = min(r, g, b);
    const mx = max(r, g, b);
    const lum = (mn + mx) / 2;
    const d = mx - mn;
    let hue = 0;
    let sat = 0;

    if (d !== 0) {
      sat = lum === 0 || lum === 1 ? 0 : (mx - lum) / min(lum, 1 - lum);
      switch (mx) {
        case r:
          hue = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          hue = (b - r) / d + 2;
          break;
        case b:
          hue = (r - g) / d + 4;
          break;
      }
      hue /= 6;
    }
    if (sat < 0) {
      hue += 0.5;
      sat = -sat;
    }
    hue -= floor(hue);
    return [hue * 360, sat * 100, lum * 100];
  }
  // https://drafts.csswg.org/css-color/#rgb-to-hwb
  private static _rgbHue(r: number, g: number, b: number) {
    // hue [0, 360]
    const mn = min(r, g, b);
    const mx = max(r, g, b);
    let hue = 0;
    const d = mx - mn;

    if (d !== 0) {
      switch (mx) {
        case r:
          hue = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          hue = (b - r) / d + 2;
          break;
        case b:
          hue = (r - g) / d + 4;
          break;
      }
      hue /= 6;
    }
    hue -= floor(hue);
    return hue * 360;
  }
  // https://drafts.csswg.org/css-color/#hwb-to-rgb
  private static _hwbToRgb(hue: number, wh: number, bk: number) {
    // hue [0, 360]  wh [0, 100]  bk [0, 100]
    wh /= 100;
    bk /= 100;
    if (wh + bk >= 1) {
      const gray = wh / (wh + bk);
      return [gray, gray, gray];
    }
    const rgb = Color._hslToRgb(hue, 100, 50);
    for (let i = 0; i < 3; i++) {
      rgb[i] = rgb[i] * (1 - wh - bk) + wh;
    }
    return rgb;
  }
  // https://drafts.csswg.org/css-color/#rgb-to-hwb
  private static _rgbToHwb(r: number, g: number, b: number) {
    let hue = Color._rgbHue(r, g, b);
    const wh = min(r, g, b);
    const bk = 1 - max(r, g, b);
    if (wh + bk >= 1 - 1e-5) hue = 0;

    return [hue, wh * 100, bk * 100];
  }
  private static _applyTfm(tfm: number[], x: number) {
    const sign = x < 0 ? -1 : 1;
    x *= sign;

    const [g, a, b, c, d, e, f] = tfm;

    return sign * (x < d ? c * x + f : Math.pow(a * x + b, g) + e);
  }
  private static _rgbTfm: number[] = [
    2.4, 0.9478672985781991, 0.05213270142180095, 0.07739938080495357, 0.04045,
    0, 0,
  ];
  private static _invRgbTfm: number[] = [
    0.4166666666666667, 1.1371189658248804, 0, 12.92, 0.0031308049535603718,
    -0.05499999999999994, 0,
  ];
  // https://drafts.csswg.org/css-color-4/#color-conversion-code
  private static _xyzToRgb(x: number, y: number, z: number) {
    const r =
      x * 3.134112153323845 +
      y * -1.6173924595518705 +
      z * -0.49063340456472027;
    const g =
      x * -0.9787872961391832 +
      y * 1.9162795864266209 +
      z * 0.03345471423138195;
    const b =
      x * 0.07198304438569493 +
      y * -0.22898585024754495 +
      z * 1.4053851315844992;

    return [
      this._applyTfm(this._invRgbTfm, r),
      this._applyTfm(this._invRgbTfm, g),
      this._applyTfm(this._invRgbTfm, b),
    ];
  }
  private static _rgbToXyz(r: number, g: number, b: number) {
    r = this._applyTfm(this._rgbTfm, r);
    g = this._applyTfm(this._rgbTfm, g);
    b = this._applyTfm(this._rgbTfm, b);

    const x =
      r * 0.436065673828125 + g * 0.3851470947265625 + b * 0.14306640625;
    const y =
      r * 0.2224884033203125 + g * 0.7168731689453125 + b * 0.06060791015625;
    const z =
      r * 0.013916015625 + g * 0.097076416015625 + b * 0.7140960693359375;

    return [x, y, z];
  }
  private static _D50 = [0.9642, 1, 0.8251];
  private static _labToRgb(lum: number, a: number, b: number) {
    // L [0, 100]  a [-125, 125]  b [-125, 125]
    const Kf = 108 / 841;
    const sub = 16 / 116;
    const e = 6 / 29;

    lum = min(max(lum, 0), 100);

    // compute f, starting with the luminance-related term
    const fy = (lum + 16) / 116;
    const fx = fy + a / 500;
    const fz = fy - b / 200;

    // compute xyz, then XYZ by scaling xyz by reference white
    const X = (fx > e ? fx * fx * fx : (fx - sub) * Kf) * Color._D50[0];
    const Y = (fy > e ? fy * fy * fy : (fy - sub) * Kf) * Color._D50[1];
    const Z = (fz > e ? fz * fz * fz : (fz - sub) * Kf) * Color._D50[2];

    // finally, convert from XYZ to rgb space
    return Color._xyzToRgb(X, Y, Z);
  }
  private static _rgbToLab(r: number, g: number, b: number) {
    // L [0, 100]  a [-125, 125]  b [-125, 125]
    // convert from rgb to XYZ space
    let [x, y, z] = Color._rgbToXyz(r, g, b);
    console.log(x, y, z);

    const E = 216 / 24389; // 6^3/29^3
    const K = 24389 / 27; // 29^3/3^3

    // compute xyz, which is XYZ scaled relative to reference white
    x /= Color._D50[0];
    y /= Color._D50[1];
    z /= Color._D50[2];

    // now compute f
    const fx = x > E ? cbrt(x) : (K * x + 16) / 116;
    const fy = y > E ? cbrt(y) : (K * y + 16) / 116;
    const fz = z > E ? cbrt(z) : (K * z + 16) / 116;

    return [
      116 * fy - 16, // L
      500 * (fx - fy), // a
      200 * (fy - fz), // b
    ];
  }
  private static _lchToRgb(lum: number, chr: number, hue: number) {
    // lum [0, 100]  chr [0, 125]  hue [0, 360]
    hue *= DTR;
    // Convert from polar form
    return Color._labToRgb(
      lum, // L
      chr * cos(hue), // a
      chr * sin(hue) // b
    );
  }
  private static _rgbToLch(r: number, g: number, b: number) {
    const [lum, A, B] = Color._rgbToLab(r, g, b);

    const chr = sqrt(A * A + B * B);
    let hue = atan2(B, A) / PI2;
    hue = (hue - floor(hue)) * 360;

    return [lum, chr, hue];
  }
  private static _oklabToRgb(lum: number, a: number, b: number) {
    // Given OKLab, convert to XYZ relative to D65
    lum /= 100;
    const L =
      (lum * 0.9999999984505198 +
        a * 0.39633779217376786 +
        b * 0.2158037580607588) **
      3;
    const M =
      (lum * 1.0000000088817609 +
        a * -0.10556134232365635 +
        b * -0.06385417477170591) **
      3;
    const S =
      (lum * 1.0000000546724108 +
        a * -0.08948418209496575 +
        b * -1.2914855378640917) **
      3;

    const X =
      L * 1.163127464068502 + M * -0.5288292329538384 + S * 0.266769115466926;
    const Y =
      L * -0.03846732260271643 + M * 1.0544890231473 + S * -0.06798474155604696;
    const Z =
      L * -0.07240437901881265 +
      M * -0.3995912490392088 +
      S * 1.5044626260965128;

    return Color._xyzToRgb(X, Y, Z);
  }
  private static _rgbToOklab(r: number, g: number, b: number) {
    const [X, Y, Z] = Color._rgbToXyz(r, g, b);

    // Given XYZ relative to D65, convert to OKLab
    // caution: cbrt correctly returns a signed cube root
    const L = cbrt(
      X * 0.8190224432164319 + Y * 0.3619062562801221 + Z * -0.12887378261216414
    );
    const M = cbrt(
      X * 0.0329836671980271 + Y * 0.9292868468965546 + Z * 0.03614466816999844
    );
    const S = cbrt(
      X * 0.048177199566046255 +
        Y * 0.26423952494422764 +
        Z * 0.6335478258136937
    );

    const lum =
      L * 0.21922071892322073 +
      M * 0.8266759022284973 +
      S * -0.0042416677472855106;
    const A =
      L * 2.0603919436399765 + M * -2.5297551165810512 + S * 0.4693631729410756;
    const B =
      L * 0.02698306873377712 +
      M * 0.8153780929473239 +
      S * -0.8423611228273712;

    return [lum * 100, A, B];
  }
  private static _oklchToRgb(lum: number, chr: number, hue: number) {
    // lum [0, 100]  chr [0, ?]  hue [0, 360]
    hue *= DTR;
    // Convert from polar form
    return Color._oklabToRgb(
      lum, // L
      chr * cos(hue), // a
      chr * sin(hue) // b
    );
  }
  private static _rgbToOklch(r: number, g: number, b: number) {
    const [lum, A, B] = Color._rgbToOklab(r, g, b);

    const chr = Math.sqrt(A * A + B * B);
    let hue = Math.atan2(B, A) / PI2;
    hue = (hue - floor(hue)) * 360;

    return [lum, chr, hue];
  }
  private static _blendHue(hue1: number, hue2: number, T: number) {
    const diff = (hue2 - hue1) % 360;
    const shortest = ((diff * 2) % 360) - diff;
    return ((hue1 % 360) + shortest * T + 360) % 360;
  }
}

// utilities
const { min, max, floor, abs, cbrt, sqrt, sin, cos, atan2, PI } = Math;
const PI2 = PI * 2;
const DTR = PI / 180;

const buildRegex = (pttn: string) => {
  return new RegExp(
    "^" +
      pttn.replace(
        /[ XR,0/]/g,
        (m) =>
          ({
            " ": "\\s*",
            X: "[0-9a-f]",
            R: "(deg|rad|turn)",
            ",": "(?:\\s*,\\s*|\\s+)",
            "0": "(none|[+-]?(?:\\d+\\.\\d+|\\.\\d+|\\d+\\.?)(?:[eE][+-]?\\d+)?)",
            "/": "(?:\\s*[,\\/]\\s*|\\s+)",
          }[m]!)
      ) +
      "$",
    "i"
  );
};
const hex = (n: number) => ((n & 255) + 256).toString(16).slice(1);
const unhex = (a: string, b: string) => parseInt(a + b, 16);
const printSlashAlpha = (a: number) => (a >= 1 ? "" : ` / ${a}`);

// css regexps
const rgxHexColor = buildRegex("#(X{3}|X{4}|X{6}|X{8})");
const rgxRgbColor = buildRegex("rgba? \\( 0(%)?,0(%)?,0(%)?(?:/0(%)?)? \\)");
const rgxHslColor = buildRegex("hsla? \\( 0R?,0%?,0%?(?:/0(%)?)? \\)");
const rgxHwbColor = buildRegex("hwb \\( 0R?,0%?,0%?(?:/0(%)?)? \\)");
const rgxLabColor = buildRegex("(ok)?lab \\( 0(%)?,0(%)?,0(%)?(?:/0(%)?)? \\)");
const rgxLchColor = buildRegex("(ok)?lch \\( 0(%)?,0(%)?,0R?(?:/0(%)?)? \\)");

const cssRotUnitTable = { rad: 1 / DTR, deg: 1, turn: 360 }; // convert unit to degrees
Object.setPrototypeOf(cssRotUnitTable, null);
type CSSRotationUnit = keyof typeof cssRotUnitTable;

// prettier-ignore
const cssNamedColors = {
  indianred:            "cd5c5c",  lightcoral:      "f08080",  salmon:           "fa8072",
  darksalmon:           "e9967a",  lightsalmon:     "ffa07a",  crimson:          "dc143c",
  red:                  "ff0000",  firebrick:       "b22222",  darkred:          "8b0000",
  pink:                 "ffc0cb",  lightpink:       "ffb6c1",  hotpink:          "ff69b4",
  deeppink:             "ff1493",  mediumvioletred: "c71585",  palevioletred:    "db7093",
  coral:                "ff7f50",  tomato:          "ff6347",  orangered:        "ff4500",
  darkorange:           "ff8c00",  orange:          "ffa500",  gold:             "ffd700",
  yellow:               "ffff00",  lightyellow:     "ffffe0",  lemonchiffon:     "fffacd",
  lightgoldenrodyellow: "fafad2",  papayawhip:      "ffefd5",  moccasin:         "ffe4b5",
  peachpuff:            "ffdab9",  palegoldenrod:   "eee8aa",  khaki:            "f0e68c",
  darkkhaki:            "bdb76b",  lavender:        "e6e6fa",  thistle:          "d8bfd8",
  plum:                 "dda0dd",  violet:          "ee82ee",  orchid:           "da70d6",
  fuchsia:              "ff00ff",  magenta:         "ff00ff",  mediumorchid:     "ba55d3",
  mediumpurple:         "9370db",  blueviolet:      "8a2be2",  darkviolet:       "9400d3",
  darkorchid:           "9932cc",  darkmagenta:     "8b008b",  purple:           "800080",
  rebeccapurple:        "663399",  indigo:          "4b0082",  mediumslateblue:  "7b68ee",
  slateblue:            "6a5acd",  darkslateblue:   "483d8b",  greenyellow:      "adff2f",
  chartreuse:           "7fff00",  lawngreen:       "7cfc00",  lime:             "00ff00",
  limegreen:            "32cd32",  palegreen:       "98fb98",  lightgreen:       "90ee90",
  mediumspringgreen:    "00fa9a",  springgreen:     "00ff7f",  mediumseagreen:   "3cb371",
  seagreen:             "2e8b57",  forestgreen:     "228b22",  green:            "008000",
  darkgreen:            "006400",  yellowgreen:     "9acd32",  olivedrab:        "6b8e23",
  olive:                "808000",  darkolivegreen:  "556b2f",  mediumaquamarine: "66cdaa",
  darkseagreen:         "8fbc8f",  lightseagreen:   "20b2aa",  darkcyan:         "008b8b",
  teal:                 "008080",  aqua:            "00ffff",  cyan:             "00ffff",
  lightcyan:            "e0ffff",  paleturquoise:   "afeeee",  aquamarine:       "7fffd4",
  turquoise:            "40e0d0",  mediumturquoise: "48d1cc",  darkturquoise:    "00ced1",
  cadetblue:            "5f9ea0",  steelblue:       "4682b4",  lightsteelblue:   "b0c4de",
  powderblue:           "b0e0e6",  lightblue:       "add8e6",  skyblue:          "87ceeb",
  lightskyblue:         "87cefa",  deepskyblue:     "00bfff",  dodgerblue:       "1e90ff",
  cornflowerblue:       "6495ed",  royalblue:       "4169e1",  blue:             "0000ff",
  mediumblue:           "0000cd",  darkblue:        "00008b",  navy:             "000080",
  midnightblue:         "191970",  cornsilk:        "fff8dc",  blanchedalmond:   "ffebcd",
  bisque:               "ffe4c4",  navajowhite:     "ffdead",  wheat:            "f5deb3",
  burlywood:            "deb887",  tan:             "d2b48c",  rosybrown:        "bc8f8f",
  sandybrown:           "f4a460",  goldenrod:       "daa520",  darkgoldenrod:    "b8860b",
  peru:                 "cd853f",  chocolate:       "d2691e",  saddlebrown:      "8b4513",
  sienna:               "a0522d",  brown:           "a52a2a",  maroon:           "800000",
  white:                "ffffff",  snow:            "fffafa",  honeydew:         "f0fff0",
  mintcream:            "f5fffa",  azure:           "f0ffff",  aliceblue:        "f0f8ff",
  ghostwhite:           "f8f8ff",  whitesmoke:      "f5f5f5",  seashell:         "fff5ee",
  beige:                "f5f5dc",  oldlace:         "fdf5e6",  floralwhite:      "fffaf0",
  ivory:                "fffff0",  antiquewhite:    "faebd7",  linen:            "faf0e6",
  lavenderblush:        "fff0f5",  mistyrose:       "ffe4e1",  gainsboro:        "dcdcdc",
  lightgray:            "d3d3d3",  lightgrey:       "d3d3d3",  silver:           "c0c0c0",
  darkgray:             "a9a9a9",  darkgrey:        "a9a9a9",  gray:             "808080",
  grey:                 "808080",  dimgray:         "696969",  dimgrey:          "696969",
  lightslategray:       "778899",  lightslategrey:  "778899",  slategray:        "708090",
  slategrey:            "708090",  darkslategray:   "2f4f4f",  darkslategrey:    "2f4f4f",
  black:                "000000",  transparent:     "00000000",
};
Object.setPrototypeOf(cssNamedColors, null);
type CSSColorName = keyof typeof cssNamedColors;

const validColorSpaces: Record<ColorSpace, true> = {
  rgb: true,
  hsl: true,
  hwb: true,
  lab: true,
  lch: true,
  oklab: true,
  oklch: true,
};
Object.setPrototypeOf(validColorSpaces, null);
